import { i18n } from '@/main.js'
import { router } from '@/router'
import {
  fetchToken,
  fetchMetadata,
  fetchServices,
  fetchInspectionReportUrl,
  fetchAdvisor,
  fetchTax,
  postServices,
  postLog,
  fetchStripePaymentMethods,
  putMediaTracking
} from '@/services'

// Serializes trackMediaView's calls to putMediaTracking so they always hit
// the API one at a time, in the order media was viewed, instead of racing
// each other when the user navigates slides faster than the API responds.
let mediaTrackingQueue = Promise.resolve()

export const actions = {
  async getMetadata ({ state, commit, dispatch }) {
    commit('setLoading', { key: 'getMetadata', loading: true })
    await fetchMetadata()
      .then((response) => {
        const {
          dealerContactInfo,
          declinedReasons,
          firstName,
          lastName,
          isBusiness,
          customerConcernsLabel,
          customerCommentsLabel,
          supportedLanguages,
          serviceCategories,
          local,
          promise,
          responseBy,
          expired,
          topImageUrl,
          financingOptUrls,
          dealerId,
          inspectionLevelMediaUrl,
          inspectionLevelMediaUrls,
          isDisableDeclineReasons,
          isGuestPaymentEnabled,
          authenticationAnswer,
          isReadOnly,
          isCompletedByAdvisor,
          customerSignature
        } = response

        const order = {
          1: 2,
          2: 3,
          3: 4,
          4: 5,
          5: 1
        }
        let categories = serviceCategories.map((category) => {
          return {
            ...category,
            sortOrder: order[category.id]
          }
        })
        categories.sort((a, b) => a.sortOrder - b.sortOrder)
        categories = categories.map((category) => {
          if (category.id === '5') {
            return {
              ...category,
              name: customerConcernsLabel
            }
          } else { return category }
        })
        commit('setCustomerCommentsLabel', customerCommentsLabel)
        commit('setLanguages', supportedLanguages)
        commit('setCustomer', { firstName, lastName, isBusiness })
        commit('setCategories', categories)
        commit('setReasons', declinedReasons)
        commit('setDefaultLocale', local)
        if (!state.locale.selected) {
          commit('setLocale', local)
          i18n.locale = state.locale.selected
        }
        commit('setRespondBy', responseBy)
        commit('setReadyBy', promise)
        commit('setExpired', expired)
        commit('setDealer', dealerContactInfo)
        commit('setDmpiLinks', financingOptUrls)
        commit('setTopImageUrl', topImageUrl || '')
        commit('setDealerId', dealerId)
        commit('setInspectionLevelMediaUrl', inspectionLevelMediaUrl)
        commit('setinspectionLevelMediaUrls', inspectionLevelMediaUrls)
        // Reset isViewed for this session regardless of what the API returned,
        // so each media gets tracked at most once per session but is not
        // skipped just because it was already viewed in a previous session.
        const inspectionLevelMedia = response.inspectionLevelMedia != null
          ? response.inspectionLevelMedia.map((media) => ({ ...media, isViewed: false }))
          : response.inspectionLevelMedia
        commit('setInspectionLevelMedia', inspectionLevelMedia)
        commit('setDisableDeclineReasons', isDisableDeclineReasons)
        commit('setReadOnly', isReadOnly)
        commit('setCompletedByAdvisor', isCompletedByAdvisor)
        commit('setCustomerSignature', customerSignature)
        commit('setGuestPaymentEnabled', isGuestPaymentEnabled)
        commit('setCode', authenticationAnswer)
        commit('setHazardousMaterialsFee', response. totalApprovedHazardousMaterialFee || 0)
        commit('setGuestPaymentSurchargeAmountOnline', response.guestPaymentSurchargeAmountOnline || 0)
        commit('setGuestPaymentSurchargeAmountTerminal', response.guestPaymentSurchargeAmountTerminal || 0)
        commit('setGuestPaymentSurchargeDisclaimer', response.guestPaymentSurchargeDisclaimer || null)
        commit('setGuestPaymentSurchargePriceBreakdown', response.guestPaymentSurchargePriceBreakdown || false)
        dispatch('routeAfterMetadata')
      })
      .then(() => {
        if (state.code && !state.expired) {
          dispatch('logIn')
        }
      })
      .catch((error) => {
        dispatch('handleError', error)
      })
      .finally(() => {
        commit('setLoading', { key: 'getMetadata', loading: false })
      })
  }, 
  async getStripePaymentMethods ({ commit, dispatch }) {
    commit('setLoading', { key: 'getStripePaymentMethods', loading: true })
    await fetchStripePaymentMethods()
      .then((responses) => {
        commit('setStripePaymentMethods', responses)
      })
      .catch((error) => {
        commit('setStripePaymentMethods', [])
      })
      .finally(() => {
        commit('setLoading', { key: 'getStripePaymentMethods', loading: false })
      })
  },
  async getTax ({ commit, dispatch, getters, state }) {
    // Use Set to ensure unique IDs (avoid duplicates from standalone sublets)
    const selectedServiceIdsSet = new Set()
    
    // Helper function to add sublet service IDs
    const addSubletServiceIds = (parentService) => {
      if (parentService.subletServices && parentService.subletServices.length > 0) {
        parentService.subletServices.forEach((sublet) => {
          if (sublet.isSelected) {
            selectedServiceIdsSet.add(sublet.id)
          }
        })
      }
    }
    
    state.services.forEach((service) => {
      if (service.category !== '3' && service.category !== '4') {
        if (service.subServices) {
          service.subServices.forEach((subService) => {
            if (subService.isSelected) {
              selectedServiceIdsSet.add(subService.id)
              // Add child sublet IDs
              addSubletServiceIds(subService)
            }
          })
        } else {
          if (service.isSelected) {
            selectedServiceIdsSet.add(service.id)
            // Add child sublet IDs
            addSubletServiceIds(service)
          }
        }
      }
    })

    // Convert Set to Array and filter out previously approved
    const previouslyApprovedServiceIds = [...new Set(getters.previouslyApprovedServices.map(service => service.id))]
    let selectedServiceIds = [...selectedServiceIdsSet].filter(id => !previouslyApprovedServiceIds.includes(id))

    commit('setLoading', { key: 'getTax', loading: true })

    Promise.all([fetchTax(selectedServiceIds), fetchTax(previouslyApprovedServiceIds)])
      .then((responses) => {
        const [newlyApprovedServicesTaxResponse, previouslyApprovedServicesTaxResponse] = responses
        commit('setNewlyApprovedTax', newlyApprovedServicesTaxResponse.taxAndFee)
        commit('setCoreCharges', newlyApprovedServicesTaxResponse.coreCharges)
        commit('setApprovedHazardousMaterialFee', newlyApprovedServicesTaxResponse.hazardousMaterialFee)
        commit('setPreviouslyApprovedTax', previouslyApprovedServicesTaxResponse.taxAndFee)
      })
      .catch((error) => {
        error.message = i18n.t('we_couldnt_get_tax')
        dispatch('handleError', error)
      })
      .finally(() => {
        commit('setLoading', { key: 'getTax', loading: false })
      })
  },
  async handleError ({ state, commit }, e) {
    let text = i18n.t('something_went_wrong')
    try {
      if (e.message) {
        text = e.message
      } else if (e.response) {
        await e.response.json().then((response) => {
          if (typeof response.error === 'string') {
            text = `${i18n.t('were_sorry_we_dont_recognize')} ${state.code} ${i18n.t('in_our_database')}`
          }
        })
      }
    } catch (e) {
      console.log('DMPI error: ', e)
    }
    commit('setError', text)
  },
  async logIn ({ commit, dispatch }) {
    commit('setLoading', { key: 'logIn', loading: true })
    await fetchToken()
      .then((response) => {
        commit('setToken', response.access_token)
        Promise.all([fetchServices(), fetchInspectionReportUrl(), fetchAdvisor()])
          .then((responses) => {
            const [servicesResponse, inspectionReportUrlResponse, advisor] = responses

            let additional = false
            const parentServices = []
            for (const service of servicesResponse) {
              service.isApproveSelected = false;
              service.isDeferSelected = false;
              // Reset isViewed for this session regardless of what the API
              // returned, so each media gets tracked at most once per session
              // but is not skipped just because it was already viewed in a
              // previous session.
              if (service.itemLevelMedia != null) {
                service.itemLevelMedia.forEach((media) => {
                  media.isViewed = false;
                });
              }
              if(service.isApproved !== null && service.isApproved !== undefined )
              {
                if(service.isApproved)
                {
                  service.isApproveSelected = true;
                  service.isDeferSelected = false;
                }
  
                if(!service.isApproved)
                {
                  service.isApproveSelected = false;
                  service.isDeferSelected = true;
                  service.isSelected = false;
                } 
              }

              if (
                typeof service.declinedReasonId === 'number' &&
                service.declinedReasonId > -1 &&
                service.declinedReasonId !== 8
              ) {
                service.deferred = true
              } else {
                service.deferred = false
              }
              if (service.isHighlighted) {
                additional = true
              }
              if (!service.parentServiceId) {
                if (!parentServices.some((duplicate) => duplicate.id === service.id)) {
                  parentServices.push(service)
                }
              }
            }

            if (additional) {
              for (const service of servicesResponse) {
                if (service.deferred) {
                  if (
                    service.category === '1' ||
                    service.category === '2' ||
                    service.category === '6' ||
                    service.category === '7'
                  ) {
                    service.isSelected = false
                  }
                }
              }
            }

            for (const parentService of parentServices) {
              const subServices = servicesResponse
                .filter((subService) => subService.parentServiceId === parentService.id)
              if (subServices.length) {
                parentService.subServices = subServices
              }
            }

            // Process sublet services
            // First, create a map of all services by id for quick lookup (including subServices)
            const allServicesMap = new Map()
            for (const ps of parentServices) {
              allServicesMap.set(ps.id, ps)
              if (ps.subServices) {
                for (const sub of ps.subServices) {
                  allServicesMap.set(sub.id, sub)
                }
              }
            }
            
            // For standalone sublets, filter out duplicate records
            // Standalone sublets may have two records with similar IDs:
            // - "203461-0" (parent-level with suffix)
            // - "203461" (child-level without suffix)
            // If both have no parentServiceId, both end up in parentServices causing duplicates
            // We keep only one record per standalone sublet (prefer the one without "-0" suffix)
            const standaloneSubletIdsToRemove = new Set()
            const standaloneSubletBaseIds = new Map() // baseId -> service
            
            for (const service of parentServices) {
              if (service.isStandAloneSublet === true) {
                // Extract base ID (remove "-0", "-1", etc. suffix if present)
                const match = service.id.match(/^(\d+)(-\d+)?$/)
                if (match) {
                  const baseId = match[1]
                  const hasSuffix = !!match[2]
                  
                  if (standaloneSubletBaseIds.has(baseId)) {
                    // Duplicate found - keep the one without suffix, or the first one
                    const existing = standaloneSubletBaseIds.get(baseId)
                    const existingHasSuffix = existing.id.includes('-')
                    
                    if (hasSuffix && !existingHasSuffix) {
                      // Current has suffix, existing doesn't - remove current
                      standaloneSubletIdsToRemove.add(service.id)
                    } else if (!hasSuffix && existingHasSuffix) {
                      // Current doesn't have suffix, existing does - remove existing
                      standaloneSubletIdsToRemove.add(existing.id)
                      standaloneSubletBaseIds.set(baseId, service)
                    } else {
                      // Both have or don't have suffix - remove the later one
                      standaloneSubletIdsToRemove.add(service.id)
                    }
                  } else {
                    standaloneSubletBaseIds.set(baseId, service)
                  }
                }
              }
            }
            
            // Remove duplicate standalone sublet records from parentServices
            for (let i = parentServices.length - 1; i >= 0; i--) {
              if (standaloneSubletIdsToRemove.has(parentServices[i].id)) {
                parentServices.splice(i, 1)
              }
            }
            
            for (const service of servicesResponse) {
              // Handle child sublets - services that have parentServiceLineIdForSublet
              // These should be grouped under their parent service with shared approve/decline
              // Use the CHILD record (the one WITH parentServiceId, which has clean ID without suffix)
              // Each sublet has two records:
              // - "203461-0" with parentServiceId: "" (parent-level - skip this one)
              // - "203461" with parentServiceId: "203461-0" (child record - USE THIS ONE, has clean ID)
              if (
                service.isStandAloneSublet === false && 
                service.parentServiceLineIdForSublet !== null && 
                service.parentServiceLineIdForSublet !== undefined &&
                service.parentServiceId && service.parentServiceId !== ''
              ) {
                const parentLineIdStr = String(service.parentServiceLineIdForSublet)
                
                // Find the target service by EXACT id match
                // The service could be a parent OR a subService (child)
                // e.g., parentServiceLineIdForSublet: 183939 → find service with id: "183939"
                let targetServiceForSublet = allServicesMap.get(parentLineIdStr)
                
                if (targetServiceForSublet) {
                  // Add this sublet to subletServices array of the target service
                  if (!targetServiceForSublet.subletServices) {
                    targetServiceForSublet.subletServices = []
                  }
                  // Check if not already added (by id)
                  if (!targetServiceForSublet.subletServices.some((sub) => sub.id === service.id)) {
                    targetServiceForSublet.subletServices.push(service)
                  }
                }
              }
            }

            commit('setServices', parentServices)
            commit('setAdvisor', advisor)
            commit('setInspectionReportUrl', inspectionReportUrlResponse.fullInspectionUrl)
            dispatch('routeAfterLogin')
          })
      })
      .catch((error) => {
        dispatch('handleError', error)
        commit('setLoading', { key: 'logIn', loading: false })
      })
  },
  sendLog () {
    postLog()
      .catch((error) => {
        console.log({ error })
      })
  },
  async sendServices ({ commit, dispatch }) {
    commit('setLoading', { key: 'sendServices', loading: true })
    await postServices()
      .then(() => {
        dispatch('routeAfterApprove')
      })
      .catch((error) => {
        dispatch('handleError', error)
      })
      .finally(() => {
        commit('setLoading', { key: 'sendServices', loading: false })
      })
  },
  routeAfterApprove ({ commit }) {
    router.push({ name: 'thanks' })
    commit('setExpired', true)
  },
  routeAfterLogin ({ commit, getters, state }) {
    if (getters.additionalServices.length) {
      if (state.route.query.src) {
        router.push({ name: 'additional',  query: { src:state.route.query.src } })
      } else {
        router.push({ name: 'additional' })
      }
    } else {
      if (state.route.query.src) { 
        router.push({ name: 'at-a-glance', query: { src: state.route.query.src } })
      } else {
        router.push({ name: 'at-a-glance'})
      } 
    }
    commit('setLoading', { key: 'logIn', loading: false })
  },
  routeAfterMetadata ({ state }) {
    if (state.expired) {
      router.push({ name: 'link-expired'})
    }
  },
  viewService ({ commit, state, dispatch }, service) {
    dispatch('viewServiceInner', { service, mediaIndex: 0 })
  },
  viewServiceInner ({ commit, state, dispatch }, { service, mediaIndex }) {    
    if (service == null && (state.inspectionLevelMediaUrls != null || state.inspectionLevelMedia != null)) {
      let serviceOnject = {
        category: "",
        comment: "",
        declinedReasonId: null,
        deferred: false,
        iconUrl: null,
        id: "",
        selectedImageURL: service,
        imageUrl: state.inspectionLevelMediaUrl,
        imageUrls: state.inspectionLevelMediaUrls,
        images: state.inspectionLevelMedia,
        isHighlighted: false,
        isSelected: false,
        isApproveSelected: false,
        isDeferSelected: false,
        isApproved: null,
        laborMatrixPayment: "",
        laborMatrixPaymentTranslations: null,
        name: "",
        parentServiceId: "",
        price: null,
        serviceAdvisorComments: "",
        taxAndFee: 0,
        hideFooter: true,
        oldApp: state.inspectionLevelMediaUrls === null ? true : false,
      };
      commit("setService", serviceOnject);
      if (!state.modal) {
        commit("openService");
      }
      if (state.inspectionLevelMedia != null && state.inspectionLevelMedia.length > 0) {
        dispatch('trackMediaView', state.inspectionLevelMedia[mediaIndex || 0].id);
      }
    } else if (typeof service === "string") {
      let serviceOnject = {
        category: "",
        comment: "",
        declinedReasonId: null,
        deferred: false,
        iconUrl: null,
        id: "",
        selectedImageURL: service,
        imageUrl: state.inspectionLevelMediaUrl,
        imageUrls: state.inspectionLevelMediaUrls,
        images: state.inspectionLevelMedia,
        isHighlighted: false,
        isSelected: false,
        isApproveSelected: false,
        isDeferSelected: false,
        isApproved: null,
        laborMatrixPayment: "",
        laborMatrixPaymentTranslations: null,
        name: "",
        parentServiceId: "",
        price: null,
        serviceAdvisorComments: "",
        taxAndFee: 0,
        hideFooter: true,
        fromInspection: true,
        oldApp: state.inspectionLevelMediaUrls === null ? true : false,
      };
      commit("setService", serviceOnject);
      if (!state.modal) {
        commit("openService");
      }
      if (state.inspectionLevelMedia != null && state.inspectionLevelMedia.length > 0) {
        dispatch('trackMediaView', state.inspectionLevelMedia[mediaIndex || 0].id);
      }
    } else {
      if (state.service.id !== undefined) {
        commit("logEvent", `Finished viewing service ${service.id}`);
      }
      service["fromInspection"] = false;
      service["oldApp"] =
        state.inspectionLevelMediaUrls === null ? true : false;
      commit("setService", service);
      commit("logEvent", `Started viewing service ${service.id}`);
      if (!state.modal) {
        commit("openService");
      }
      if (service != null && service.itemLevelMedia != null && service.itemLevelMedia.length > 0) {
        dispatch('trackMediaView', service.itemLevelMedia[mediaIndex || 0].id);
      }
    }
  },
  async trackMediaView ({ commit, state }, mediaId) {
    let media = null;
    if (state.inspectionLevelMedia != null) {
      const index = state.inspectionLevelMedia.findIndex(
        (media) => media.id === mediaId,
      );
      if (index !== -1) {
        media = state.inspectionLevelMedia[index];
        if (media.isViewed) {
          return;
        }
        media.isViewed = true;
        commit("setInspectionLevelMedia", [...state.inspectionLevelMedia]);
      }
    }
    if (media == null && state.services != null) {
      for (const service of state.services) {
        if (service.itemLevelMedia == null) {
          continue;
        }
        const index = service.itemLevelMedia.findIndex(
          (image) => image.id === mediaId,
        );
        if (index !== -1) {
          media = service.itemLevelMedia[index];
          if (media.isViewed) {
            return;
          }
          media.isViewed = true;
          commit("setServices", [...state.services]);
          if (state.service != null && state.service.id === service.id) {
            commit("setService", { ...service });
          }
          break;
        }
      }
    }
    if (media != null) {
      // Chain onto the shared queue instead of firing in parallel: rapid slide
      // navigation dispatches trackMediaView without awaiting it, and sending
      // overlapping PUTs for the same token has been observed to get one of
      // them rejected (403) by infrastructure in front of the tracking API.
      // Queueing keeps requests in view order and always one at a time.
      mediaTrackingQueue = mediaTrackingQueue.then(() => putMediaTracking(mediaId));
      await mediaTrackingQueue;
    }
  }
}

export default actions
